import type { BranaCreateBody, PujcovnaPoskytovatel } from './api';

/** The form of a new gateway version: the provider's own fields by key, the mode, the methods and the card window. */
export interface BranaForm {
  provider: string;
  rezim: 'test' | 'live';
  hodnoty: Record<string, string>;
  metody: string[];
  okno: string;
}

export const OKNO_MIN = 5;
export const OKNO_MAX = 1440;
export const OKNO_VYCHOZI = 75;

export const METODA_NAZEV: Record<string, string> = {
  karta: 'Platební karta',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
};

/** A fresh form for a provider: empty fields, the card method on, the default window. */
export function branaSeed(p: PujcovnaPoskytovatel): BranaForm {
  return { provider: p.id, rezim: p.rezimy.includes('test') ? 'test' : (p.rezimy[0] ?? 'test'), hodnoty: Object.fromEntries(p.pole.map((f) => [f.klic, ''])), metody: p.metody.includes('karta') ? ['karta'] : [], okno: String(OKNO_VYCHOZI) };
}

/** Switching provider starts that provider's form (the other one's values do not apply). */
export function branaSeProvidenem(form: BranaForm, p: PujcovnaPoskytovatel): BranaForm {
  if (form.provider === p.id) return form;
  const seed = branaSeed(p);
  // the chosen mode carries over when the new provider supports it
  return { ...seed, rezim: p.rezimy.includes(form.rezim) ? form.rezim : seed.rezim };
}

export function parseOkno(text: string): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const n = Number(text.trim());
  return n >= OKNO_MIN && n <= OKNO_MAX ? n : null;
}

/** The request body, or why the form cannot be sent (Czech, shown as it is). The server judges the rest. */
export function branaBody(form: BranaForm, p: PujcovnaPoskytovatel): { ok: true; body: BranaCreateBody } | { ok: false; chyba: string } {
  for (const f of p.pole) {
    if ((form.hodnoty[f.klic] ?? '').trim() === '') return { ok: false, chyba: `Vyplňte pole „${f.popisek}“.` };
  }
  if (form.metody.length === 0) return { ok: false, chyba: 'Vyberte alespoň jednu platební metodu.' };
  const okno = parseOkno(form.okno);
  if (okno == null) return { ok: false, chyba: `Platnost platby kartou musí být ${OKNO_MIN} až ${OKNO_MAX} minut.` };
  return {
    ok: true,
    body: {
      provider: p.id,
      rezim: form.rezim,
      hodnoty: Object.fromEntries(p.pole.map((f) => [f.klic, (form.hodnoty[f.klic] ?? '').trim()])),
      metody: form.metody,
      okno_karty_min: okno,
    },
  };
}

/** `2026-10-29 14:05:11.123` → `29. 10. 2026 14:05` (the API's Prague-local text; anything else is shown as it is). */
export function datumCas(text: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(text);
  return m ? `${Number(m[3])}. ${Number(m[2])}. ${m[1]} ${m[4]}:${m[5]}` : text;
}
